import { useEffect, useMemo, useState } from 'react';
import {
  LayoutDashboard,
  ShoppingBag,
  UtensilsCrossed,
  Users,
  Settings,
  LogOut,
  Menu,
  X,
  Package,
  Tag,
  TrendingUp,
  Clock,
  IndianRupee,
  Bell,
  CheckCircle,
  AlertCircle,
  ChevronRight,
  ArrowUp,
  ArrowDown,
  BarChart3,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import type { User } from '../../App';
import { getCategories, getMenuItems, getOrders, getRestaurants } from '../../lib/mockData';
import { api } from '../../lib/api';
import { OrdersView } from '../restaurant/OrdersView';
import { MenuManagement } from '../restaurant/MenuManagement';
import { StaffManagement } from '../restaurant/StaffManagement';
import { RestaurantSettings } from '../restaurant/RestaurantSettings';
import { CouponManagement } from '../restaurant/CouponManagement';
import { OfferMaster } from '../restaurant/OfferMaster';
import { Analytics } from '../restaurant/Analytics';
import { ToastContainer, useToast } from '../ui/Toast';

interface RestaurantAdminDashboardProps {
  user: User;
  onLogout: () => void;
}

type Tab = 'dashboard' | 'orders' | 'menu' | 'offers' | 'coupons' | 'staff' | 'analytics' | 'settings';
type TimeRange = 'today' | 'week' | 'month' | 'year';

type DashboardOrder = {
  id: string;
  orderNumber: string;
  status: string;
  total: number;
  createdAt: string;
  customerName: string;
  itemsCount: number;
};

type DashboardSummary = {
  restaurant: any;
  metrics: {
    totalOrders: number;
    todayOrders: number;
    activeOrders: number;
    pendingOrders: number;
    todayRevenue: number;
    rating: number;
    totalReviews: number;
  };
  menuOverview: {
    totalItems: number;
    activeItems: number;
    categories: number;
  };
  orderStatusCounts: Record<string, number>;
  recentOrders: DashboardOrder[];
};

type AnalyticsData = {
  metrics: {
    totalRevenue: number;
    totalOrders: number;
    averageOrderValue: number;
    uniqueCustomers: number;
    repeatRate: number;
    completionRate: number;
    activeMenuItems: number;
    revenueGrowth: number;
    orderGrowth: number;
    averageRating: number;
    totalReviews: number;
  };
  charts: {
    revenueTrend: Array<{ name: string; revenue: number; orders: number }>;
    orderStatusDistribution: Array<{ key: string; name: string; value: number; color: string }>;
    peakHours: Array<{ hour: string; orders: number }>;
    topSellingItems: Array<{ id: string; name: string; quantity: number; revenue: number }>;
  };
};

const ACTIVE_ORDER_STATUSES = new Set(['PENDING', 'CONFIRMED', 'PREPARING', 'READY', 'OUT_FOR_DELIVERY']);

const emptyAnalytics = (): AnalyticsData => ({
  metrics: {
    totalRevenue: 0,
    totalOrders: 0,
    averageOrderValue: 0,
    uniqueCustomers: 0,
    repeatRate: 0,
    completionRate: 0,
    activeMenuItems: 0,
    revenueGrowth: 0,
    orderGrowth: 0,
    averageRating: 0,
    totalReviews: 0,
  },
  charts: {
    revenueTrend: [],
    orderStatusDistribution: [],
    peakHours: [],
    topSellingItems: [],
  },
});

const formatOrderStatus = (status: string) =>
  String(status || 'PENDING')
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const getStatusBadgeClass = (status: string) => {
  switch (String(status || '').toUpperCase()) {
    case 'DELIVERED':
    case 'COMPLETED':
      return 'bg-green-50 text-green-700';
    case 'PREPARING':
    case 'READY':
      return 'bg-blue-50 text-blue-700';
    case 'OUT_FOR_DELIVERY':
      return 'bg-orange-50 text-orange-700';
    case 'CONFIRMED':
      return 'bg-purple-50 text-purple-700';
    case 'CANCELLED':
      return 'bg-red-50 text-red-700';
    default:
      return 'bg-gray-50 text-gray-700';
  }
};

const buildFallbackSummary = (restaurantId: string, restaurant: any): DashboardSummary => {
  const orders = getOrders(restaurantId || '');
  const menuItems = getMenuItems(restaurantId || '');
  const categories = getCategories(restaurantId || '');
  const today = new Date().toDateString();
  const todayOrders = orders.filter((order) => new Date(order.createdAt).toDateString() === today);
  const pendingOrders = orders.filter((order) => ['pending', 'confirmed'].includes(String(order.status || '').toLowerCase())).length;
  const activeOrders = orders.filter((order) =>
    ACTIVE_ORDER_STATUSES.has(String(order.status || '').toUpperCase())
  ).length;

  return {
    restaurant: {
      ...restaurant,
      rating: Number(restaurant?.rating || 0),
      totalReviews: Number(restaurant?.totalReviews || 0),
    },
    metrics: {
      totalOrders: orders.length,
      todayOrders: todayOrders.length,
      activeOrders,
      pendingOrders,
      todayRevenue: todayOrders.reduce((sum, order) => sum + Number(order.total || 0), 0),
      rating: Number(restaurant?.rating || 0),
      totalReviews: Number(restaurant?.totalReviews || 0),
    },
    menuOverview: {
      totalItems: menuItems.length,
      activeItems: menuItems.filter((item) => item.isActive).length,
      categories: categories.length,
    },
    orderStatusCounts: orders.reduce<Record<string, number>>((acc, order) => {
      const key = String(order.status || '').toUpperCase();
      acc[key] = Number(acc[key] || 0) + 1;
      return acc;
    }, {}),
    recentOrders: orders.slice(0, 5).map((order) => {
      const rawOrder = order as Record<string, any>;

      return {
        id: String(rawOrder.id || ''),
        orderNumber: String(rawOrder.orderNumber || rawOrder.id || ''),
        status: String(rawOrder.status || 'PENDING').toUpperCase(),
        total: Number(rawOrder.total || 0),
        createdAt: String(rawOrder.createdAt || new Date().toISOString()),
        customerName: String(rawOrder.customerName || 'Customer'),
        itemsCount: Array.isArray(rawOrder.items) ? rawOrder.items.length : 0,
      };
    }),
  };
};

export function RestaurantAdminDashboard({ user, onLogout }: RestaurantAdminDashboardProps) {
  const { toasts, removeToast, info, error: toastError } = useToast();
  const [activeTab, setActiveTab] = useState<Tab>('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [timeRange, setTimeRange] = useState<TimeRange>('today');
  const [showNotifications, setShowNotifications] = useState(false);
  const [apiRestaurant, setApiRestaurant] = useState<any | null>(null);
  const [dashboardSummary, setDashboardSummary] = useState<DashboardSummary | null>(null);
  const [analytics, setAnalytics] = useState<AnalyticsData>(emptyAnalytics());
  const [staffCount, setStaffCount] = useState(0);
  const [activeStaffCount, setActiveStaffCount] = useState(0);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [logoLoadFailed, setLogoLoadFailed] = useState(false);
  const [restaurantIsOpen, setRestaurantIsOpen] = useState<boolean | null>(null);
  const [restaurantStatusLoading, setRestaurantStatusLoading] = useState(false);
  const [restaurantStatusSaving, setRestaurantStatusSaving] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  const restaurants = getRestaurants();
  const resolvedRestaurantId =
    user.restaurantId || (user as any)?.restaurant_id || (user as any)?.restaurant?.id || restaurants[0]?.id || '';
  const localRestaurant = restaurants.find((item) => item.id === resolvedRestaurantId);
  const baseRestaurant =
    apiRestaurant ||
    localRestaurant ||
    (resolvedRestaurantId
      ? {
          id: resolvedRestaurantId,
          name: (user as any)?.name ? `${(user as any).name} Restaurant` : 'Restaurant',
          openTime: '09:00',
          closeTime: '22:00',
          rating: 0,
          totalReviews: 0,
        }
      : null);
  const restaurant = dashboardSummary?.restaurant || baseRestaurant;
  const settingsRestaurant = {
    ...(baseRestaurant || {}),
    ...(dashboardSummary?.restaurant || {}),
    ...(apiRestaurant || {}),
  };
  const summary = dashboardSummary || (restaurant ? buildFallbackSummary(resolvedRestaurantId, restaurant) : null);
  const currencySource =
    settingsRestaurant?.currency ||
    (settingsRestaurant as any)?.currencyMaster ||
    (settingsRestaurant as any)?.currencyRef ||
    (restaurant as any)?.currency ||
    null;
  const currencySymbol =
    currencySource?.symbol ||
    (settingsRestaurant as any)?.priceRangeMaster?.symbol ||
    '₹';
  const currencyDecimals = Math.max(
    2,
    Number.isFinite(Number(currencySource?.decimalPlaces)) ? Number(currencySource?.decimalPlaces) : 2
  );
  const formatMoney = (value: number | string) => {
    const amount = Number(value ?? 0);
    const safe = Number.isFinite(amount) ? amount : 0;
    return `${currencySymbol}${safe.toLocaleString('en-IN', {
      minimumFractionDigits: currencyDecimals,
      maximumFractionDigits: currencyDecimals,
    })}`;
  };
  const restaurantLogo = String(restaurant?.logo || '').trim();
  const restaurantInitials = String(restaurant?.name || user?.name || 'Restaurant')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  const notificationOrders = summary?.recentOrders.filter((order) =>
    ['PENDING', 'CONFIRMED'].includes(String(order.status || '').toUpperCase())
  ) || [];

  const alerts = useMemo(() => {
    const items: Array<{ tone: 'yellow' | 'blue' | 'green'; message: string }> = [];
    const inactiveItems = Math.max((summary?.menuOverview.totalItems || 0) - (summary?.menuOverview.activeItems || 0), 0);
    if ((summary?.metrics.pendingOrders || 0) > 0) {
      items.push({ tone: 'yellow', message: `${summary?.metrics.pendingOrders} orders need confirmation` });
    }
    if (inactiveItems > 0) {
      items.push({ tone: 'blue', message: `${inactiveItems} menu items are inactive` });
    }
    if ((analytics.metrics.completionRate || 0) >= 90) {
      items.push({ tone: 'green', message: `Completion rate is ${analytics.metrics.completionRate.toFixed(1)}%` });
    }
    return items.slice(0, 3);
  }, [analytics.metrics.completionRate, summary]);

  useEffect(() => {
    let isMounted = true;

    const loadDashboard = async () => {
      if (!resolvedRestaurantId) return;
      setDashboardLoading(true);

      try {
        const [restaurantResponse, summaryResponse, analyticsResponse, staffResponse] = await Promise.all([
          api.restaurants.getById(String(resolvedRestaurantId)),
          api.restaurants.getDashboardSummary(String(resolvedRestaurantId)),
          api.restaurants.getAnalytics(String(resolvedRestaurantId), { range: timeRange }),
          api.users.getAll({ restaurantId: String(resolvedRestaurantId), limit: 100 }),
        ]);

        if (!isMounted) return;

        setApiRestaurant(restaurantResponse?.data || null);
        setDashboardSummary(summaryResponse?.data || null);
        setAnalytics(analyticsResponse?.data || emptyAnalytics());

        const staffItems = Array.isArray(staffResponse?.data?.users) ? staffResponse.data.users : [];
        setStaffCount(staffItems.length);
        setActiveStaffCount(staffItems.filter((item: any) => item?.isActive !== false).length);
      } catch {
        if (!isMounted) return;

        try {
          const restaurantResponse = await api.restaurants.getById(String(resolvedRestaurantId));
          if (restaurantResponse?.data) {
            setApiRestaurant(restaurantResponse.data);
          }
        } catch {
          // Keep local fallback.
        }

        setDashboardSummary(null);
        setAnalytics(emptyAnalytics());
        setStaffCount(0);
        setActiveStaffCount(0);
      } finally {
        if (isMounted) {
          setDashboardLoading(false);
        }
      }
    };

    void loadDashboard();

    return () => {
      isMounted = false;
    };
  }, [resolvedRestaurantId, timeRange]);

  useEffect(() => {
    if (restaurantIsOpen !== null) return;

    const raw = (restaurant as any)?.is_open ?? (restaurant as any)?.isOpen;
    if (raw === undefined || raw === null) return;

    setRestaurantIsOpen(Boolean(Number(raw)));
  }, [restaurant, restaurantIsOpen]);

  useEffect(() => {
    let isMounted = true;

    const loadRestaurantStatus = async () => {
      if (!resolvedRestaurantId) return;
      setRestaurantStatusLoading(true);
      try {
        const response = await api.restaurants.getStatus();
        if (!isMounted) return;
        const isOpen = Number((response as any)?.data?.is_open ?? 1) === 1;
        setRestaurantIsOpen(isOpen);
      } catch {
        // Keep existing value.
      } finally {
        if (isMounted) setRestaurantStatusLoading(false);
      }
    };

    void loadRestaurantStatus();

    return () => {
      isMounted = false;
    };
  }, [resolvedRestaurantId]);

  const persistRestaurantStatus = async (nextIsOpen: boolean) => {
    if (restaurantIsOpen === null) return;
    if (restaurantStatusSaving) return;

    const previous = restaurantIsOpen;
    setRestaurantIsOpen(nextIsOpen);
    // setApiRestaurant((prev) => (prev ? { ...prev, is_open: nextIsOpen ? 1 : 0, isOpen: nextIsOpen } : prev));
    setApiRestaurant((prev: any | null) =>
      prev ? { ...prev, is_open: nextIsOpen ? 1 : 0, isOpen: nextIsOpen } : prev
    );
    setRestaurantStatusSaving(true);

    try {
      await api.restaurants.updateStatus({ is_open: nextIsOpen ? 1 : 0 });
      info(nextIsOpen ? 'Restaurant is now Online' : 'Restaurant is now Offline – no new orders will be accepted');
    } catch {
      setRestaurantIsOpen(previous);
      // setApiRestaurant((prev) => (prev ? { ...prev, is_open: previous ? 1 : 0, isOpen: previous } : prev));
      setApiRestaurant((prev: any | null) =>
        prev ? { ...prev, is_open: previous ? 1 : 0, isOpen: previous } : prev
      );
      toastError('Failed to update restaurant status. Please try again.');
    } finally {
      setRestaurantStatusSaving(false);
    }
  };

  useEffect(() => {
    setLogoLoadFailed(false);
  }, [restaurantLogo, restaurant?.id]);

  if (!restaurant || !summary) {
    return <div>No restaurant is configured for this admin account.</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <div
        className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-gradient-to-b from-blue-700 to-blue-900 text-white transition-all duration-300 flex flex-col fixed h-full z-40`}
      >
        <div className="p-4 border-b border-blue-600">
          <div className="flex items-center justify-between">
            {sidebarOpen && (
              <div>
                <h2 className="text-white">{restaurant.name}</h2>
                <p className="text-blue-200 text-sm">Restaurant Admin</p>
              </div>
            )}
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 hover:bg-blue-600 rounded-lg">
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          <button
            onClick={() => setActiveTab('dashboard')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              activeTab === 'dashboard' ? 'bg-blue-600' : 'hover:bg-blue-600/50'
            }`}
          >
            <LayoutDashboard className="w-5 h-5 flex-shrink-0" />
            {sidebarOpen && <span>Dashboard</span>}
          </button>

          <button
            onClick={() => setActiveTab('orders')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors relative ${
              activeTab === 'orders' ? 'bg-blue-600' : 'hover:bg-blue-600/50'
            }`}
          >
            <ShoppingBag className="w-5 h-5 flex-shrink-0" />
            {sidebarOpen && <span>Orders</span>}
            {summary.metrics.pendingOrders > 0 && (
              <span className="absolute right-2 top-2 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                {summary.metrics.pendingOrders}
              </span>
            )}
          </button>

          <button
            onClick={() => setActiveTab('menu')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              activeTab === 'menu' ? 'bg-blue-600' : 'hover:bg-blue-600/50'
            }`}
          >
            <UtensilsCrossed className="w-5 h-5 flex-shrink-0" />
            {sidebarOpen && <span>Menu</span>}
          </button>

          <button
            onClick={() => setActiveTab('offers')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              activeTab === 'offers' ? 'bg-blue-600' : 'hover:bg-blue-600/50'
            }`}
          >
            <Tag className="w-5 h-5 flex-shrink-0" />
            {sidebarOpen && <span>Offers</span>}
          </button>

          <button
            onClick={() => setActiveTab('coupons')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              activeTab === 'coupons' ? 'bg-blue-600' : 'hover:bg-blue-600/50'
            }`}
          >
            <Package className="w-5 h-5 flex-shrink-0" />
            {sidebarOpen && <span>Coupons</span>}
          </button>

          <button
            onClick={() => setActiveTab('staff')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              activeTab === 'staff' ? 'bg-blue-600' : 'hover:bg-blue-600/50'
            }`}
          >
            <Users className="w-5 h-5 flex-shrink-0" />
            {sidebarOpen && <span>Staff</span>}
          </button>

          <button
            onClick={() => setActiveTab('analytics')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              activeTab === 'analytics' ? 'bg-blue-600' : 'hover:bg-blue-600/50'
            }`}
          >
            <TrendingUp className="w-5 h-5 flex-shrink-0" />
            {sidebarOpen && <span>Analytics</span>}
          </button>

          <button
            onClick={() => setActiveTab('settings')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              activeTab === 'settings' ? 'bg-blue-600' : 'hover:bg-blue-600/50'
            }`}
          >
            <Settings className="w-5 h-5 flex-shrink-0" />
            {sidebarOpen && <span>Settings</span>}
          </button>
        </nav>

        <div className="p-4 border-t border-blue-600">
          <div className="flex items-center gap-3 mb-3">
            {restaurantLogo && !logoLoadFailed ? (
              <img
                src={restaurantLogo}
                alt={restaurant?.name || user.name}
                className="w-10 h-10 rounded-full object-cover"
                onError={() => setLogoLoadFailed(true)}
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-blue-500 text-white flex items-center justify-center font-semibold text-sm uppercase">
                {restaurantInitials || 'RA'}
              </div>
            )}
            {sidebarOpen && (
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm truncate">{user.name}</p>
                <p className="text-blue-200 text-xs truncate">{user.email}</p>
              </div>
            )}
          </div>
          <button
            onClick={onLogout}
            className="w-full flex items-center gap-3 px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg transition-colors"
          >
            <LogOut className="w-4 h-4 flex-shrink-0" />
            {sidebarOpen && <span>Logout</span>}
          </button>
        </div>
      </div>

      <div className={`flex-1 ${sidebarOpen ? 'ml-64' : 'ml-20'} transition-all duration-300`}>
        <div className="p-6 md:p-8">
          {activeTab === 'dashboard' && (
            <div>
              <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
                <div>
                  <h1 className="text-gray-900 mb-1">Restaurant Dashboard</h1>
                  <p className="text-gray-600">Live operations snapshot for your restaurant</p>
                  {dashboardLoading && <p className="text-xs text-gray-500 mt-1">Refreshing live dashboard...</p>}
                </div>

                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 text-gray-600 bg-white px-4 py-2 rounded-lg border border-gray-200">
                    <Clock className="w-4 h-4" />
                    <span className="text-sm">
                      {restaurant.openTime || '--:--'} - {restaurant.closeTime || '--:--'}
                    </span>
                  </div>

                  <select
                    value={timeRange}
                    onChange={(e) => setTimeRange(e.target.value as TimeRange)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    <option value="today">Today</option>
                    <option value="week">This Week</option>
                    <option value="month">This Month</option>
                    <option value="year">This Year</option>
                  </select>

                  <button
                    type="button"
                    role="switch"
                    aria-checked={restaurantIsOpen ?? undefined}
                    disabled={restaurantStatusLoading || restaurantStatusSaving || restaurantIsOpen === null}
                    onClick={() => {
                      if (restaurantIsOpen === null) return;
                      if (restaurantStatusLoading || restaurantStatusSaving) return;
                      const next = !restaurantIsOpen;
                      if (restaurantIsOpen && !next) {
                        setShowCloseConfirm(true);
                        return;
                      }
                      void persistRestaurantStatus(next);
                    }}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full border transition-colors font-semibold ${
                      restaurantIsOpen === null
                        ? 'bg-gray-100 border-gray-200 text-gray-600'
                        : restaurantIsOpen
                          ? 'bg-green-600 border-green-600 text-white'
                          : 'bg-red-50 border-red-200 text-red-700'
                    } ${restaurantStatusLoading || restaurantStatusSaving || restaurantIsOpen === null ? 'opacity-70 cursor-not-allowed' : 'hover:brightness-95'}`}
                    title={
                      restaurantIsOpen === null
                        ? 'Loading status...'
                        : restaurantIsOpen
                          ? 'Online (accepting orders)'
                          : 'Offline (not accepting new orders)'
                    }
                  >
                    <span className="text-sm">
                      {restaurantIsOpen === null ? 'Loading' : restaurantIsOpen ? 'Online' : 'Offline'}
                    </span>
                    <span
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        restaurantIsOpen ? 'bg-green-500' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                          restaurantIsOpen ? 'translate-x-5' : 'translate-x-1'
                        }`}
                      />
                    </span>
                  </button>

                  <div className="relative">
                    <button
                      onClick={() => setShowNotifications(!showNotifications)}
                      className="p-2 hover:bg-gray-100 rounded-lg relative"
                    >
                      <Bell className="w-6 h-6 text-gray-600" />
                      {summary.metrics.pendingOrders > 0 && (
                        <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
                      )}
                    </button>

                    {showNotifications && (
                      <div className="absolute right-0 mt-2 w-96 overflow-hidden bg-white rounded-xl shadow-2xl border border-blue-100 z-50">
                        <div className="p-5 border-b border-blue-100 bg-blue-50">
                          <div className="flex items-center justify-between">
                            <h3 className="text-lg font-bold text-gray-900">Notifications</h3>
                            <button
                              onClick={() => setShowNotifications(false)}
                              className="p-1 hover:bg-white rounded-lg transition-colors"
                            >
                              <X className="w-5 h-5 text-gray-600" />
                            </button>
                          </div>
                          <p className="text-sm text-blue-700 mt-2 font-medium">
                            {summary.metrics.pendingOrders} pending, {summary.orderStatusCounts?.READY || 0} ready
                          </p>
                        </div>

                        <div className="max-h-96 overflow-y-auto bg-white">
                          {notificationOrders.length > 0 ? (
                            <div className="divide-y divide-gray-100">
                              {notificationOrders.slice(0, 5).map((order) => (
                                <button
                                  key={order.id}
                                  onClick={() => {
                                    setShowNotifications(false);
                                    setActiveTab('orders');
                                  }}
                                  className="w-full p-5 hover:bg-blue-50/40 transition-colors text-left"
                                >
                                  <div className="flex items-start gap-3">
                                    <div
                                      className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 ${
                                        String(order.status).toUpperCase() === 'PENDING' ? 'bg-orange-100' : 'bg-blue-100'
                                      }`}
                                    >
                                      {String(order.status).toUpperCase() === 'PENDING' ? (
                                        <AlertCircle className="w-5 h-5 text-orange-600" />
                                      ) : (
                                        <CheckCircle className="w-5 h-5 text-blue-600" />
                                      )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center justify-between gap-3 mb-1">
                                        <p className="font-semibold text-gray-900 text-sm">
                                          {String(order.status).toUpperCase() === 'PENDING' ? 'New Order' : 'Order Confirmed'}
                                        </p>
                                        <span className="text-xs text-gray-500 truncate">{order.orderNumber}</span>
                                      </div>
                                      <div className="flex items-center gap-2 mb-1">
                                        <p className="text-sm text-gray-700">
                                          {order.customerName} - {formatMoney(order.total)}
                                        </p>
                                        <span
                                          className={`px-2 py-0.5 rounded-md text-xs font-medium ${
                                            String(order.status).toUpperCase() === 'PENDING'
                                              ? 'bg-orange-50 text-orange-700'
                                              : 'bg-blue-50 text-blue-700'
                                          }`}
                                        >
                                          {formatOrderStatus(order.status)}
                                        </span>
                                      </div>
                                      <p className="text-xs text-gray-500">
                                        {order.itemsCount} items - {new Date(order.createdAt).toLocaleTimeString('en-IN')}
                                      </p>
                                    </div>
                                    <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                  </div>
                                </button>
                              ))}
                            </div>
                          ) : (
                            <div className="p-8 text-center">
                              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                                <CheckCircle className="w-8 h-8 text-blue-600" />
                              </div>
                              <h4 className="text-gray-900 font-semibold mb-1">All Caught Up</h4>
                              <p className="text-sm text-gray-600">No pending notifications</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {alerts.length > 0 && (
                <div className="mb-6 space-y-2">
                  {alerts.map((alert, index) => (
                    <div
                      key={`${alert.message}-${index}`}
                      className={`flex items-center gap-3 p-4 rounded-lg border ${
                        alert.tone === 'yellow'
                          ? 'bg-yellow-50 border-yellow-200'
                          : alert.tone === 'green'
                            ? 'bg-green-50 border-green-200'
                            : 'bg-blue-50 border-blue-200'
                      }`}
                    >
                      <AlertCircle
                        className={`w-5 h-5 ${
                          alert.tone === 'yellow'
                            ? 'text-yellow-600'
                            : alert.tone === 'green'
                              ? 'text-green-600'
                              : 'text-blue-600'
                        }`}
                      />
                      <p className="text-sm font-medium text-gray-800">{alert.message}</p>
                    </div>
                  ))}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <button
                  onClick={() => setActiveTab('analytics')}
                  className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-white text-left"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 bg-white/20 rounded-lg">
                      <IndianRupee className="w-6 h-6" />
                    </div>
                    <div className="flex items-center gap-1 text-sm bg-white/20 px-2 py-1 rounded-full">
                      {analytics.metrics.revenueGrowth >= 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                      <span>{Math.abs(analytics.metrics.revenueGrowth).toFixed(1)}%</span>
                    </div>
                  </div>
                  <h3 className="text-white/80 text-sm mb-1">Revenue</h3>
                  <p className="text-3xl font-bold mb-1">{formatMoney(analytics.metrics.totalRevenue)}</p>
                  <p className="text-white/60 text-xs">Avg value: {formatMoney(analytics.metrics.averageOrderValue)}</p>
                </button>

                <button
                  onClick={() => setActiveTab('orders')}
                  className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white text-left"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 bg-white/20 rounded-lg">
                      <ShoppingBag className="w-6 h-6" />
                    </div>
                    <div className="flex items-center gap-1 text-sm bg-white/20 px-2 py-1 rounded-full">
                      {analytics.metrics.orderGrowth >= 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                      <span>{Math.abs(analytics.metrics.orderGrowth).toFixed(1)}%</span>
                    </div>
                  </div>
                  <h3 className="text-white/80 text-sm mb-1">Active Orders</h3>
                  <p className="text-3xl font-bold mb-1">{summary.metrics.activeOrders}</p>
                  <p className="text-white/60 text-xs">Pending: {summary.metrics.pendingOrders}</p>
                </button>

                <button
                  onClick={() => setActiveTab('staff')}
                  className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg p-6 text-white text-left"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 bg-white/20 rounded-lg">
                      <Users className="w-6 h-6" />
                    </div>
                    <div className="flex items-center gap-1 text-sm bg-white/20 px-2 py-1 rounded-full">
                      <CheckCircle className="w-3 h-3" />
                      <span>{activeStaffCount}</span>
                    </div>
                  </div>
                  <h3 className="text-white/80 text-sm mb-1">Staff On Duty</h3>
                  <p className="text-3xl font-bold mb-1">{activeStaffCount} / {staffCount}</p>
                  <p className="text-white/60 text-xs">Restaurant team</p>
                </button>

                <button
                  onClick={() => setActiveTab('menu')}
                  className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl shadow-lg p-6 text-white text-left"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 bg-white/20 rounded-lg">
                      <UtensilsCrossed className="w-6 h-6" />
                    </div>
                    <div className="flex items-center gap-1 text-sm bg-white/20 px-2 py-1 rounded-full">
                      <AlertCircle className="w-3 h-3" />
                      <span>{Math.max(summary.menuOverview.totalItems - summary.menuOverview.activeItems, 0)}</span>
                    </div>
                  </div>
                  <h3 className="text-white/80 text-sm mb-1">Active Menu Items</h3>
                  <p className="text-3xl font-bold mb-1">{summary.menuOverview.activeItems}</p>
                  <p className="text-white/60 text-xs">Total listed: {summary.menuOverview.totalItems}</p>
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                <div className="bg-white rounded-xl shadow-sm p-5 border-l-4 border-green-500">
                  <p className="text-gray-600 text-xs">Avg Order Value</p>
                  <p className="text-gray-900 text-lg font-semibold">{formatMoney(analytics.metrics.averageOrderValue)}</p>
                </div>
                <div className="bg-white rounded-xl shadow-sm p-5 border-l-4 border-blue-500">
                  <p className="text-gray-600 text-xs">Completion Rate</p>
                  <p className="text-gray-900 text-lg font-semibold">{analytics.metrics.completionRate.toFixed(1)}%</p>
                </div>
                <div className="bg-white rounded-xl shadow-sm p-5 border-l-4 border-purple-500">
                  <p className="text-gray-600 text-xs">Rating</p>
                  <p className="text-gray-900 text-lg font-semibold">
                    {analytics.metrics.averageRating > 0 ? analytics.metrics.averageRating.toFixed(1) : Number(summary.metrics.rating || 0).toFixed(1)}
                  </p>
                </div>
                <div className="bg-white rounded-xl shadow-sm p-5 border-l-4 border-orange-500">
                  <p className="text-gray-600 text-xs">Repeat Customers</p>
                  <p className="text-gray-900 text-lg font-semibold">{analytics.metrics.repeatRate.toFixed(1)}%</p>
                </div>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-8">
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-gray-900">Revenue Trend</h3>
                    <TrendingUp className="w-5 h-5 text-green-600" />
                  </div>
                  {analytics.charts.revenueTrend.length === 0 ? (
                    <div className="h-[250px] flex items-center justify-center text-gray-500">No trend data available</div>
                  ) : (
                    <ResponsiveContainer width="100%" height={250}>
                      <AreaChart data={analytics.charts.revenueTrend}>
                        <defs>
                          <linearGradient id="restaurantAdminRevenue" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#2563eb" stopOpacity={0.25} />
                            <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="name" stroke="#9ca3af" />
                        <YAxis stroke="#9ca3af" />
                        <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }} />
                        <Area type="monotone" dataKey="revenue" stroke="#2563eb" fill="url(#restaurantAdminRevenue)" strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  )}
                </div>

                <div className="bg-white rounded-xl shadow-sm p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-gray-900">Order Status</h3>
                    <BarChart3 className="w-5 h-5 text-blue-600" />
                  </div>
                  {analytics.charts.orderStatusDistribution.length === 0 ? (
                    <div className="h-[250px] flex items-center justify-center text-gray-500">No order data available</div>
                  ) : (
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie data={analytics.charts.orderStatusDistribution} cx="50%" cy="50%" outerRadius={90} dataKey="value">
                          {analytics.charts.orderStatusDistribution.map((entry) => (
                            <Cell key={entry.key} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </div>

                <div className="bg-white rounded-xl shadow-sm p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-gray-900">Peak Hours</h3>
                    <Clock className="w-5 h-5 text-orange-600" />
                  </div>
                  {analytics.charts.peakHours.length === 0 ? (
                    <div className="h-[250px] flex items-center justify-center text-gray-500">No peak hour data available</div>
                  ) : (
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={analytics.charts.peakHours}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="hour" stroke="#9ca3af" />
                        <YAxis stroke="#9ca3af" />
                        <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }} />
                        <Bar dataKey="orders" fill="#f97316" radius={[8, 8, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>

                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h3 className="text-gray-900 mb-4">Top Selling Items</h3>
                  <div className="space-y-3">
                    {analytics.charts.topSellingItems.length === 0 && (
                      <div className="text-gray-500 py-8 text-center">No item sales data available</div>
                    )}
                    {analytics.charts.topSellingItems.slice(0, 5).map((item, index) => (
                      <div key={item.id} className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{index + 1}. {item.name}</p>
                          <p className="text-xs text-gray-500">{item.quantity} sold</p>
                        </div>
                        <p className="text-sm font-semibold text-blue-700">{formatMoney(item.revenue)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 bg-white rounded-xl shadow-sm p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg text-gray-900">Recent Orders</h3>
                    <button
                      onClick={() => setActiveTab('orders')}
                      className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                    >
                      View All
                    </button>
                  </div>

                  {summary.recentOrders.length === 0 ? (
                    <div className="py-12 text-center text-gray-500">No recent orders found.</div>
                  ) : (
                    <div className="space-y-3">
                      {summary.recentOrders.map((order) => (
                        <button
                          key={order.id}
                          onClick={() => setActiveTab('orders')}
                          className="w-full flex items-center justify-between p-4 border border-gray-100 rounded-lg hover:bg-gray-50 hover:border-gray-200 transition-all text-left"
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-sm font-medium text-gray-900">{order.orderNumber}</span>
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getStatusBadgeClass(order.status)}`}>
                                {formatOrderStatus(order.status)}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600">
                              {order.customerName} - {order.itemsCount} items
                            </p>
                            <p className="text-xs text-gray-500">
                              {new Date(order.createdAt).toLocaleString('en-IN')}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-base font-medium text-gray-900">{formatMoney(order.total)}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h3 className="text-lg text-gray-900 mb-6">Menu Overview</h3>
                  <div className="space-y-3">
                    <button
                      onClick={() => setActiveTab('menu')}
                      className="w-full p-4 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors text-left"
                    >
                      <p className="text-xs text-blue-600 mb-1 font-medium">Total Items</p>
                      <p className="text-2xl text-blue-900 font-semibold">{summary.menuOverview.totalItems}</p>
                    </button>
                    <button
                      onClick={() => setActiveTab('menu')}
                      className="w-full p-4 bg-green-50 hover:bg-green-100 rounded-lg transition-colors text-left"
                    >
                      <p className="text-xs text-green-600 mb-1 font-medium">Active Items</p>
                      <p className="text-2xl text-green-900 font-semibold">{summary.menuOverview.activeItems}</p>
                    </button>
                    <button
                      onClick={() => setActiveTab('menu')}
                      className="w-full p-4 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors text-left"
                    >
                      <p className="text-xs text-purple-600 mb-1 font-medium">Categories</p>
                      <p className="text-2xl text-purple-900 font-semibold">{summary.menuOverview.categories}</p>
                    </button>
                    <button
                      onClick={() => setActiveTab('menu')}
                      className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium mt-4"
                    >
                      Manage Menu
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'orders' && (
            <div>
              <h1 className="text-gray-900 mb-6">Order Management</h1>
              <OrdersView
                restaurantId={resolvedRestaurantId}
                showTitle={false}
                currencySymbol={currencySymbol}
                currencyDecimals={currencyDecimals}
              />
            </div>
          )}

          {activeTab === 'menu' && <MenuManagement restaurantId={resolvedRestaurantId} />}

          {activeTab === 'offers' && <OfferMaster restaurantId={resolvedRestaurantId} />}

          {activeTab === 'coupons' && <CouponManagement restaurantId={resolvedRestaurantId} />}

          {activeTab === 'staff' && <StaffManagement restaurantId={resolvedRestaurantId} />}

          {activeTab === 'analytics' && (
            <Analytics
              restaurantId={resolvedRestaurantId}
              currencySymbol={currencySymbol}
              currencyDecimals={currencyDecimals}
            />
          )}

          {activeTab === 'settings' && (
            <RestaurantSettings
              restaurant={settingsRestaurant as any}
              onRestaurantUpdated={(updatedRestaurant) => {
                setApiRestaurant((prev: any) => ({
                  ...(prev || {}),
                  ...(updatedRestaurant || {}),
                }));
                setDashboardSummary((prev) =>
                  prev
                    ? {
                        ...prev,
                        restaurant: {
                          ...prev.restaurant,
                          ...(updatedRestaurant || {}),
                        },
                      }
                    : prev
                );
              }}
            />
          )}
        </div>
      </div>

      {showCloseConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Turn Restaurant Offline?</h3>
                <p className="text-gray-600 text-sm">No new orders will be accepted</p>
              </div>
            </div>
            <p className="text-gray-700 mb-6">
              Are you sure you want to turn your restaurant <span className="font-semibold">Offline</span>?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowCloseConfirm(false);
                  void persistRestaurantStatus(false);
                }}
                disabled={restaurantStatusSaving}
                className="flex-1 bg-red-600 text-white py-3 rounded-lg hover:bg-red-700 transition-colors font-semibold disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {restaurantStatusSaving ? 'Updating...' : 'Go Offline'}
              </button>
              <button
                onClick={() => setShowCloseConfirm(false)}
                className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg hover:bg-gray-300 transition-colors font-semibold"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  );
}
